import {
  Classroom,
  GlowApiError,
  Student,
  useChangeStudentOrganizationMutation,
  useUpdateEnrollmentMutation,
  useUpdateStudentClassroomMutation,
} from '@innovamat/glow-api-client';
import { CLASSROOM_TYPE } from '@innovamat/radiance-utils';
import { useQueryClient } from '@tanstack/react-query';
import { moveStudentInCache } from '../cache-actions/move-student';
import { useEducationalPlans } from './use-education-plans';
import { useOrganization } from './use-organization';

type Props = {
  currentStudent: Student | undefined;
  updateEnrollment?: boolean;
  onError?: (error: GlowApiError) => void;
  onSuccess?: (student: Student, oldStudent: Student) => void;
};

type MoveStudent = {
  onMoveStudent: (newClassroom: Classroom, newOrgId?: string) => void;
  isLoading: boolean;
};

function useMoveStudent({
  currentStudent,
  updateEnrollment = false,
  onError,
  onSuccess,
}: Props): MoveStudent {
  const queryClient = useQueryClient();

  const { organization } = useOrganization();

  const {
    mutate: updateStudentClassroomMutation,
    isPending: updateStudentClassroomMutationIsPending,
  } = useUpdateStudentClassroomMutation<GlowApiError>();

  const {
    mutate: changeStudentOrganizationMutation,
    isPending: changeStudentOrganizationIsPending,
  } = useChangeStudentOrganizationMutation<GlowApiError>();

  const {
    mutate: updateEnrollmentMutation,
    isPending: updateEnrollmentMutationIsPending,
  } = useUpdateEnrollmentMutation<GlowApiError>();

  const { getEducationalPlanByCourse } = useEducationalPlans({});

  const onMoveStudent = (newClassroom: Classroom, newOrgId?: string): void => {
    const educationalPlanId = getEducationalPlanByCourse(
      newClassroom?.courseId!
    )?.id;

    if (!newClassroom) return;

    const newStudent: Student = {
      ...currentStudent,
      educationalPlanId,
      classroomId: newClassroom.id,
      classroomName: newClassroom.name,
      courseName: newClassroom.courseName,
      courseOrder: newClassroom.courseOrder,
    };

    if (updateEnrollment) {
      const enrollmentId = currentStudent?.enrollments?.find(
        (enrollment) => enrollment?.classroomType === CLASSROOM_TYPE.JOURNEYS
      )?.id;

      if (!enrollmentId) return;

      updateEnrollmentMutation(
        {
          body: {
            classroomId: newClassroom.id,
            studentId: currentStudent?.id!,
            enrollmentId: enrollmentId,
          },
        },
        {
          onSuccess: () => {
            if (currentStudent?.classroomId && newClassroom.id) {
              moveStudentInCache({
                queryClient,
                organizationId: organization?.id!,
                fromClassroomId: currentStudent.classroomId,
                toClassroomId: newClassroom.id,
              });
            }
            onSuccess?.(newStudent, currentStudent!);
          },
          onError: (error) => {
            onError?.(error);
          },
        }
      );
      return;
    }

    if (newOrgId) {
      changeStudentOrganizationMutation(
        {
          body: {
            studentId: newStudent.id!,
            classroomId: newClassroom.id,
            schoolId: newOrgId,
          },
        },
        {
          onError: (error) => {
            onError?.(error);
          },
          onSuccess: () => {
            if (currentStudent?.classroomId && newClassroom.id) {
              moveStudentInCache({
                queryClient,
                organizationId: organization?.id!,
                fromClassroomId: currentStudent.classroomId,
                toClassroomId: newClassroom.id,
              });
            }
            onSuccess?.(newStudent, currentStudent!);
          },
        }
      );
    } else {
      updateStudentClassroomMutation(
        {
          body: {
            studentId: newStudent.id!,
            classroomId: newClassroom.id,
            pinCode: newStudent?.pinCode,
          },
        },
        {
          onError: (error) => {
            onError?.(error);
          },
          onSuccess: () => {
            if (currentStudent?.classroomId && newClassroom.id) {
              moveStudentInCache({
                queryClient,
                organizationId: organization?.id!,
                fromClassroomId: currentStudent.classroomId,
                toClassroomId: newClassroom.id,
              });
            }
            onSuccess?.(newStudent, currentStudent!);
          },
        }
      );
    }
  };

  return {
    onMoveStudent,
    isLoading:
      updateStudentClassroomMutationIsPending ||
      changeStudentOrganizationIsPending ||
      updateEnrollmentMutationIsPending,
  };
}

export { useMoveStudent };
